<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type {
    PublicAppointmentContactCompletion,
    PublicAppointmentContactSupplement,
  } from "../../../../../../../../api/public/appointment-portal"
  import { normalizePrivacyUrl } from "../../_shared/_logic/contact-privacy"
  import { contactCompletionIntroduction } from "./_logic/contactCompletion"

  const {
    availability,
    organizationDisplayName,
    organizationPrivacyUrl,
    onSave,
    onremoveOverlay,
    onshowOverlay,
    setActionButtonSnippet,
  } = $props<{
    availability: PublicAppointmentContactCompletion
    organizationDisplayName: string
    organizationPrivacyUrl: string
    onSave: (supplement: PublicAppointmentContactSupplement) => Promise<void>
    onremoveOverlay: () => void
    onshowOverlay: () => void
    setActionButtonSnippet: (snippet: Snippet | null) => void
  }>()

  let email = $state("")
  let phone = $state("")
  let accepted = $state(false)
  let pending = $state(false)
  let error = $state<string | null>(null)
  let formElement: HTMLFormElement
  let disposed = false
  const idempotencyKey = crypto.randomUUID()
  const requestEmail = $derived(availability.email !== "present")
  const requestPhone = $derived(availability.phone !== "present")
  const privacyUrl = $derived(normalizePrivacyUrl(organizationPrivacyUrl))
  const channels = $derived(
    [
      requestEmail ? "E-Mail" : null,
      requestPhone ? "Telefon, SMS und – sofern registriert – WhatsApp" : null,
    ]
      .filter(Boolean)
      .join(" sowie "),
  )

  onMount(() => {
    onshowOverlay()
    setActionButtonSnippet(actionButtons)
  })

  onDestroy(() => {
    disposed = true
    setActionButtonSnippet(null)
  })

  const save = async (event?: SubmitEvent) => {
    event?.preventDefault()

    if (pending) return

    if (!email.trim() && !phone.trim()) {
      error =
        "Bitte geben Sie eine E-Mail-Adresse oder Telefonnummer an – oder schließen Sie den Dialog."

      return
    }

    if (!accepted) {
      error = "Bitte stimmen Sie der Nutzung Ihrer Kontaktdaten zu."

      return
    }

    pending = true
    error = null
    try {
      await onSave({
        email: requestEmail ? email.trim() || null : null,
        phone: requestPhone ? phone.trim() || null : null,
        contact_use_accepted: true,
        idempotency_key: idempotencyKey,
      })

      if (!disposed) onremoveOverlay()
    } catch {
      error =
        "Ihre Kontaktdaten konnten nicht gespeichert werden. Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut. Ihr Termin bleibt bestätigt."
    } finally {
      pending = false
    }
  }
</script>

    <form bind:this={formElement} class="contact-completion form" onsubmit={save}>
      <p>{contactCompletionIntroduction(availability)}</p>
      <p>
        So kann {organizationDisplayName} Sie bei Rückfragen und zur Abstimmung künftiger Termine einfacher
        erreichen.
      </p>
      <p>Die Angabe ist freiwillig. Ihr Termin ist bereits bestätigt.</p>
      {#if requestEmail}
        <Input
      id="contact-completion-email"
      type="email"
      placeholder="E-Mail-Adresse (freiwillig)"
      bind:value={email}
      autocomplete="email"
      surfaceTone="light"
      disabled={pending}
    />
      {/if}
      {#if requestPhone}
        <Input
      id="contact-completion-phone"
      type="tel"
      placeholder="Telefonnummer (freiwillig)"
      bind:value={phone}
      autocomplete="tel"
      surfaceTone="light"
      disabled={pending}
    />
      {/if}
      <ActionBox label="Datenschutz" darkMode={true}>
        <div class="privacy-content">
          <p class="privacy">
            Ihre Angaben werden an {organizationDisplayName} übermittelt und ausschließlich für Rückfragen
            sowie die Kommunikation zu aktuellen und zukünftigen Terminen verwendet – nicht für Werbung. KehrBlatt
            stellt die technische Plattform bereit. Weitere Informationen finden Sie in den
            {#if privacyUrl}<a href={privacyUrl} target="_blank" rel="noreferrer"
            >Datenschutzhinweisen von {organizationDisplayName}</a
          >.{:else}Datenschutzhinweisen des Betriebs.{/if}
            Kontaktwege können Sie im Terminportal unter „Kontaktwege“ ändern oder deaktivieren, sobald sie
            zugeordnet sind.
          </p>
          <Input
        id="contact-completion-consent"
        type="checkbox"
        bind:value={accepted}
        placeholder={`Ich habe die Datenschutzhinweise zur Kenntnis genommen und bin damit einverstanden, über die angegebenen Kontaktwege (${channels}) zur Terminkommunikation kontaktiert zu werden.`}
        surfaceTone="light"
        disabled={pending}
      />
        </div>
      </ActionBox>
      {#if error}<p class="error" role="alert">{error}</p>{/if}
    </form>
  {#snippet actionButtons()}
    <div class="actions">
      <CTA
      type="primaryButton"
      content={pending ? "Wird gespeichert…" : "Kontaktdaten speichern"}
      onClick={() => formElement?.requestSubmit()}
      disabled={pending}
      darkMode={false}
    />
    </div>
  {/snippet}

<style>
  .contact-completion {
    display: grid;
    gap: 1rem;
    color: var(--text-invers-100);
  }
  p {
    margin: 0;
    line-height: 1.45;
  }
  .privacy {
    font-size: 0.875rem;
  }
  .privacy-content {
    display: grid;
    gap: 0.75rem;
  }
  a {
    color: inherit;
    text-decoration: underline;
  }
  .error {
    color: #b42318;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
